import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Table, Badge, Alert } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';
import { WorkflowSubtaskType, Workflow } from '../types';
import { useModal } from '../context/ModalContext';

interface SubtaskTypesManagerProps {
  show: boolean;
  onHide: () => void;
  workflow: Workflow;
}

const SubtaskTypesManager: React.FC<SubtaskTypesManagerProps> = ({
  show,
  onHide,
  workflow,
}) => {
  const [subtaskTypes, setSubtaskTypes] = useState<WorkflowSubtaskType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState<WorkflowSubtaskType | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const { showAlert, showConfirm } = useModal();

  // Estados do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowIdForSubtask, setWorkflowIdForSubtask] = useState<string>('');
  const [defaultPriority, setDefaultPriority] = useState('p3');
  const [order, setOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (show && workflow?.id) {
      fetchSubtaskTypes();
      fetchWorkflows();
    }
  }, [show, workflow?.id]);

  const fetchSubtaskTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/${workflow.id}/subtask-types`);
      setSubtaskTypes(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar tipos de subtarefas:', error);
      showAlert({
        title: 'Erro',
        message: 'Erro ao carregar tipos de subtarefas',
        variant: 'danger',
        details: error.response?.data?.detail || 'Erro desconhecido'
      });
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/published`);
      setWorkflows(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar workflows:', error);
    }
  };

  const handleNewType = () => {
    setName('');
    setDescription('');
    setWorkflowIdForSubtask('');
    setDefaultPriority('p3');
    setOrder(subtaskTypes.length);
    setIsActive(true);
    setEditingType(null);
    setShowTypeModal(true);
  };

  const handleEditType = (type: WorkflowSubtaskType) => {
    setName(type.name);
    setDescription(type.description || '');
    setWorkflowIdForSubtask(type.workflow_id_for_subtask?.toString() || '');
    setDefaultPriority(type.default_priority);
    setOrder(type.order);
    setIsActive(type.is_active);
    setEditingType(type);
    setShowTypeModal(true);
  };

  const handleSaveType = async () => {
    if (!name.trim()) {
      showAlert({
        title: 'Erro',
        message: 'Nome do tipo de subtarefa é obrigatório',
        variant: 'danger'
      });
      return;
    }

    setLoading(true);
    try {
      const typeData = {
        name: name.trim(),
        description: description.trim() || undefined,
        workflow_id_for_subtask: workflowIdForSubtask ? parseInt(workflowIdForSubtask) : undefined,
        default_priority: defaultPriority,
        order: order,
        is_active: isActive
      };

      if (editingType?.id) {
        await axios.put(
          `${API_URL}/api/workflows/${workflow.id}/subtask-types/${editingType.id}`,
          typeData
        );
        showAlert({
          title: 'Sucesso',
          message: 'Tipo de subtarefa atualizado com sucesso!',
          variant: 'success'
        });
      } else {
        await axios.post(
          `${API_URL}/api/workflows/${workflow.id}/subtask-types`,
          typeData
        );
        showAlert({
          title: 'Sucesso',
          message: 'Tipo de subtarefa criado com sucesso!',
          variant: 'success'
        });
      }

      await fetchSubtaskTypes();
      setShowTypeModal(false);
      resetForm();
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: 'Erro ao salvar tipo de subtarefa',
        variant: 'danger',
        details: error.response?.data?.detail || 'Erro desconhecido'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteType = async (type: WorkflowSubtaskType) => {
    const ok = await showConfirm({
      title: 'Desativar tipo de subtarefa',
      message: `Tem certeza que deseja desativar o tipo de subtarefa "${type.name}"?`,
      variant: 'warning',
      confirmLabel: 'Desativar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await axios.delete(`${API_URL}/api/workflows/${workflow.id}/subtask-types/${type.id}`);
      showAlert({
        title: 'Sucesso',
        message: 'Tipo de subtarefa desativado com sucesso!',
        variant: 'success'
      });
      await fetchSubtaskTypes();
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: 'Erro ao desativar tipo de subtarefa',
        variant: 'danger',
        details: error.response?.data?.detail || 'Erro desconhecido'
      });
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setWorkflowIdForSubtask('');
    setDefaultPriority('p3');
    setOrder(0);
    setIsActive(true);
    setEditingType(null);
  };

  const handleClose = () => {
    resetForm();
    setShowTypeModal(false);
  };

  // Ordenar tipos por order e depois por nome
  const sortedTypes = [...subtaskTypes].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Tipos de Subtarefas - {workflow.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {workflow.is_published && (
            <Alert variant="warning" className="mb-3">
              <Alert.Heading>Atenção</Alert.Heading>
              <p>Este fluxo está publicado. Para fazer alterações, crie uma nova versão.</p>
            </Alert>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0">
              Configure os tipos de subtarefas disponíveis para este fluxo. Cada tipo pode ter um fluxo associado que será usado quando a subtarefa for criada.
            </p>
            <Button variant="success" size="sm" onClick={handleNewType} disabled={workflow.is_published}>
              + Novo Tipo
            </Button>
          </div>

          {sortedTypes.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">Nenhum tipo de subtarefa configurado ainda.</p>
              <Button variant="outline-primary" onClick={handleNewType} disabled={workflow.is_published}>
                Criar Primeiro Tipo
              </Button>
            </div>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Ordem</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Fluxo da Subtarefa</th>
                  <th>Prioridade Padrão</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedTypes.map((type) => {
                  const subtaskWorkflow = workflows.find(w => w.id === type.workflow_id_for_subtask);
                  return (
                    <tr key={type.id}>
                      <td>{type.order}</td>
                      <td><strong>{type.name}</strong></td>
                      <td>
                        <small className="text-muted">
                          {type.description || '-'}
                        </small>
                      </td>
                      <td>
                        {subtaskWorkflow ? (
                          <Badge bg="info">{subtaskWorkflow.name}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Badge bg={
                          type.default_priority === 'p1' ? 'danger' :
                          type.default_priority === 'p2' ? 'warning' :
                          type.default_priority === 'p3' ? 'info' :
                          'success'
                        }>
                          {type.default_priority?.toUpperCase() || 'P3'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={type.is_active ? 'success' : 'secondary'}>
                          {type.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleEditType(type)}
                          disabled={workflow.is_published}
                          title={workflow.is_published ? "Não é possível editar tipos de subtarefa de um fluxo publicado" : "Editar tipo"}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteType(type)}
                          disabled={workflow.is_published}
                          title={workflow.is_published ? "Não é possível desativar tipos de subtarefa de um fluxo publicado" : "Desativar tipo"}
                        >
                          Desativar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Criar/Editar Tipo */}
      <Modal show={showTypeModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingType ? 'Editar Tipo de Subtarefa' : 'Novo Tipo de Subtarefa'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Tipo <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Suporte Técnico, Análise, Aprovação"
                required
              />
              <Form.Text className="text-muted">
                Nome que aparecerá ao criar uma subtarefa
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrição opcional do tipo de subtarefa"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fluxo para Subtarefa</Form.Label>
              <Form.Select
                value={workflowIdForSubtask}
                onChange={(e) => setWorkflowIdForSubtask(e.target.value)}
              >
                <option value="">Nenhum (usar fluxo do ticket pai)</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Selecione qual fluxo esta subtarefa deve seguir. Se não selecionar, usará o fluxo do ticket pai.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Prioridade Padrão</Form.Label>
              <Form.Select
                value={defaultPriority}
                onChange={(e) => setDefaultPriority(e.target.value)}
              >
                <option value="p1">P1</option>
                <option value="p2">P2</option>
                <option value="p3">P3</option>
                <option value="p4">P4</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ordem</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
              />
              <Form.Text className="text-muted">
                Ordem de exibição (menor número aparece primeiro)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Ativo"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <Form.Text className="text-muted">
                Tipos inativos não aparecerão ao criar subtarefas
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveType} disabled={loading}>
            {loading ? 'Salvando...' : editingType ? 'Atualizar' : 'Criar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SubtaskTypesManager;


